import type {
  OverlayElement,
  ShapeOverlay,
  PolygonOverlay,
  TextOverlay,
  ImageOverlay,
  BlurOverlay,
  RGBA,
  Bounds,
  ResizeHandle,
} from '@/types';
import { calculateAnimationTransform } from '@/utils/animationUtils';
import { drawBlurredRegion } from '@/utils/blurEffect';

// Convert RGBA to CSS string
export const rgbaToString = (color: RGBA): string => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
};

// Draw a shape overlay
const drawShape = (
  ctx: CanvasRenderingContext2D,
  overlay: ShapeOverlay,
  scale: number
) => {
  const { bounds, style, rotation, cornerRadius } = overlay;
  const { x, y, width, height } = bounds;
  
  ctx.save();
  
  // Apply rotation
  if (rotation !== 0) {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    ctx.translate(centerX * scale, centerY * scale);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-centerX * scale, -centerY * scale);
  }
  
  // Fold this overlay's own opacity into whatever alpha the caller set (the
  // animation's). Assigning here instead of multiplying is what made every
  // fade animation a no-op.
  ctx.globalAlpha *= style.opacity / 100;
  
  // Draw based on type
  ctx.beginPath();
  
  switch (overlay.type) {
    case 'rectangle':
      ctx.rect(x * scale, y * scale, width * scale, height * scale);
      break;
      
    case 'rounded-rectangle': {
      const r = (cornerRadius ?? 12) * scale;
      const sx = x * scale;
      const sy = y * scale;
      const sw = width * scale;
      const sh = height * scale;
      
      ctx.moveTo(sx + r, sy);
      ctx.lineTo(sx + sw - r, sy);
      ctx.quadraticCurveTo(sx + sw, sy, sx + sw, sy + r);
      ctx.lineTo(sx + sw, sy + sh - r);
      ctx.quadraticCurveTo(sx + sw, sy + sh, sx + sw - r, sy + sh);
      ctx.lineTo(sx + r, sy + sh);
      ctx.quadraticCurveTo(sx, sy + sh, sx, sy + sh - r);
      ctx.lineTo(sx, sy + r);
      ctx.quadraticCurveTo(sx, sy, sx + r, sy);
      break;
    }
      
    case 'circle':
    case 'ellipse': {
      const centerX = (x + width / 2) * scale;
      const centerY = (y + height / 2) * scale;
      const radiusX = (width / 2) * scale;
      const radiusY = (height / 2) * scale;
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
      break;
    }
  }
  
  ctx.closePath();
  
  // Fill
  ctx.fillStyle = rgbaToString(style.fillColor);
  ctx.fill();
  
  // Stroke
  if (style.borderWidth > 0) {
    ctx.strokeStyle = rgbaToString(style.borderColor);
    ctx.lineWidth = style.borderWidth * scale;
    ctx.stroke();
  }
  
  ctx.restore();
};

// Draw a polygon overlay
const drawPolygon = (
  ctx: CanvasRenderingContext2D,
  overlay: PolygonOverlay,
  scale: number
) => {
  const { bounds, style, rotation, polygon } = overlay;
  const { x, y } = bounds;
  
  ctx.save();
  
  // Apply rotation
  if (rotation !== 0) {
    const centerX = x + bounds.width / 2;
    const centerY = y + bounds.height / 2;
    ctx.translate(centerX * scale, centerY * scale);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-centerX * scale, -centerY * scale);
  }
  
  ctx.globalAlpha *= style.opacity / 100;
  
  ctx.beginPath();
  polygon.points.forEach((point, index) => {
    const px = (x + point.x) * scale;
    const py = (y + point.y) * scale;
    if (index === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  });
  ctx.closePath();
  
  ctx.fillStyle = rgbaToString(style.fillColor);
  ctx.fill();
  
  if (style.borderWidth > 0) {
    ctx.strokeStyle = rgbaToString(style.borderColor);
    ctx.lineWidth = style.borderWidth * scale;
    ctx.stroke();
  }
  
  ctx.restore();
};

// Helper to draw tiles
const drawTiledOverlay = (
  ctx: CanvasRenderingContext2D,
  overlay: TextOverlay | ImageOverlay,
  scale: number,
  drawSingle: (ctx: CanvasRenderingContext2D, x: number, y: number) => void
) => {
  const { tile, rotation } = overlay;
  if (!tile?.enabled) {
    // Draw single instance if tiling is disabled
    const { x, y, width, height } = overlay.bounds;
    
    ctx.save();
    
    if (rotation !== 0) {
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      ctx.translate(centerX * scale, centerY * scale);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-centerX * scale, -centerY * scale);
    }
    
    // Apply opacity here for non-tiled
    ctx.globalAlpha *= overlay.style.opacity / 100;
    
    drawSingle(ctx, x * scale, y * scale);
    ctx.restore();
    return;
  }

  // TILING LOGIC
  const { gap, angle, opacity } = tile;
  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;
  
  // Use tile opacity or fallback to style opacity
  const tileOpacity = opacity !== undefined ? opacity : overlay.style.opacity;
  
  ctx.save();
  ctx.globalAlpha *= tileOpacity / 100;
  
  // Rotate the entire canvas pattern if angle is set
  if (angle !== 0) {
    const cx = canvasWidth / 2;
    const cy = canvasHeight / 2;
    ctx.translate(cx, cy);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.translate(-cx, -cy);
  }
  
  const width = overlay.bounds.width * scale;
  const height = overlay.bounds.height * scale;
  const stepX = width + gap * scale;
  const stepY = height + gap * scale;
  
  // Calculate coverage area (overshoot to cover rotation)
  const diag = Math.sqrt(canvasWidth * canvasWidth + canvasHeight * canvasHeight);
  const startX = -diag;
  const startY = -diag;
  const endX = diag * 2;
  const endY = diag * 2;
  
  for (let y = startY; y < endY; y += stepY) {
    // Offset every other row for brick pattern look
    const rowOffset = (Math.floor((y - startY) / stepY) % 2) * (stepX / 2);
    for (let x = startX; x < endX; x += stepX) {
      drawSingle(ctx, x + rowOffset, y);
    }
  }
  
  ctx.restore();
};

// Draw a text overlay
const drawText = (
  ctx: CanvasRenderingContext2D,
  overlay: TextOverlay,
  scale: number
) => {
  const { bounds, style, text } = overlay;
  
  const drawSingleText = (context: CanvasRenderingContext2D, drawX: number, drawY: number) => {
    const { width, height } = bounds;
    const w = width * scale;
    const h = height * scale;

    // Draw background if enabled (local to the text item)
    if (text.hasBackground && text.backgroundColor) {
      context.fillStyle = rgbaToString(text.backgroundColor);
      context.fillRect(drawX, drawY, w, h);
    }
    
    // Set font
    const fontStyle = text.fontStyle === 'italic' ? 'italic ' : '';
    const fontWeight = text.fontWeight === 'bold' ? 'bold ' : '';
    context.font = `${fontStyle}${fontWeight}${text.fontSize * scale}px ${text.fontFamily}`;
    context.fillStyle = rgbaToString(style.fillColor);
    context.textAlign = text.textAlign;
    context.textBaseline = 'middle';
    
    // Calculate text position local to drawX/drawY
    let textX = drawX;
    if (text.textAlign === 'center') {
      textX = drawX + w / 2;
    } else if (text.textAlign === 'right') {
      textX = drawX + w;
    }
    // Honour explicit line breaks. fillText draws a single line and silently
    // renders "\n" as a blank glyph, so multi-line captions used to come out
    // as one squashed run of text.
    const lines = String(text.content ?? '').split(/\r?\n/);
    const lineHeight = text.fontSize * scale * 1.2;
    const blockTop = drawY + h / 2 - ((lines.length - 1) * lineHeight) / 2;

    lines.forEach((line, i) => {
      // No maxWidth argument: passing one makes the canvas horizontally
      // squash long text instead of letting it overflow, which never matches
      // what the exporter or the backend produce.
      context.fillText(line, textX, blockTop + i * lineHeight);
    });
  };

  drawTiledOverlay(ctx, overlay, scale, drawSingleText);
};

// Draw an image overlay
const imageCache = new Map<string, HTMLImageElement>();

const drawImage = (
  ctx: CanvasRenderingContext2D,
  overlay: ImageOverlay,
  scale: number,
  onNeedsRepaint?: () => void
) => {
  const { bounds, image } = overlay;
  
  // Get or create cached image
  let img = imageCache.get(image.src);
  if (!img) {
    img = new Image();
    imageCache.set(image.src, img);
    // Attach the handler before setting src, or a cached/data: URI can finish
    // decoding before we are listening.
    img.onload = () => onNeedsRepaint?.();
    img.src = image.src;
  }
  
  if (!img.complete || img.naturalWidth === 0) {
    // Nothing to draw yet. The old code left an empty onload here and relied
    // on "the next render" — but a logo dropped onto a paused video produces
    // no further renders, so it stayed invisible until something else moved.
    const pending = img;
    const previous = pending.onload;
    pending.onload = (event) => {
      if (typeof previous === 'function') previous.call(pending, event as Event);
      onNeedsRepaint?.();
    };
    return;
  }
  
  const bitmap = img;
  const drawSingleImage = (context: CanvasRenderingContext2D, drawX: number, drawY: number) => {
    const boxW = bounds.width * scale;
    const boxH = bounds.height * scale;

    if (!image.preserveAspectRatio) {
      context.drawImage(bitmap, drawX, drawY, boxW, boxH);
      return;
    }

    // Fit the bitmap inside the box without distorting it — preserveAspectRatio
    // was stored on every image overlay and honoured nowhere.
    const ratio = Math.min(boxW / bitmap.naturalWidth, boxH / bitmap.naturalHeight);
    const drawW = bitmap.naturalWidth * ratio;
    const drawH = bitmap.naturalHeight * ratio;
    context.drawImage(
      bitmap,
      drawX + (boxW - drawW) / 2,
      drawY + (boxH - drawH) / 2,
      drawW,
      drawH
    );
  };

  drawTiledOverlay(ctx, overlay, scale, drawSingleImage);
};

// Draw a blur region overlay with actual blur effect
const drawBlur = (
  ctx: CanvasRenderingContext2D,
  overlay: BlurOverlay,
  scale: number,
  videoElement: HTMLVideoElement | null
) => {
  const { bounds, style, blur, rotation } = overlay;
  const { x, y, width, height } = bounds;

  ctx.save();
  ctx.globalAlpha *= style.opacity / 100;

  // Blur regions used to ignore rotation completely: the selection box and
  // handles rotated but the blurred patch stayed axis-aligned, so a rotated
  // region covered the wrong pixels.
  if (rotation !== 0) {
    const centerX = (x + width / 2) * scale;
    const centerY = (y + height / 2) * scale;
    ctx.translate(centerX, centerY);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);
  }

  let painted = false;

  if (videoElement && videoElement.readyState >= 2 && videoElement.videoWidth > 0) {
    // Overlay bounds are in the source video's own pixel space, which is
    // exactly the space drawImage wants for its source rect — so the sample
    // rect is the bounds, and the destination is that rect at preview scale.
    painted = drawBlurredRegion(
      ctx,
      videoElement,
      { x, y, width, height },
      { x: x * scale, y: y * scale, width: width * scale, height: height * scale },
      { blurType: blur.blurType, intensity: blur.intensity },
      { width: videoElement.videoWidth, height: videoElement.videoHeight }
    );
  }

  if (!painted) {
    // No frame to sample (or a tainted canvas) — show the placeholder rather
    // than leaving the region untouched and looking like the blur is broken.
    drawBlurPlaceholder(ctx, overlay, scale);
  }

  // Draw border
  if (style.borderWidth > 0) {
    ctx.strokeStyle = rgbaToString(style.borderColor);
    ctx.lineWidth = style.borderWidth * scale;
    ctx.strokeRect(x * scale, y * scale, width * scale, height * scale);
  }

  ctx.restore();
};

// Placeholder for blur when video is not available
const drawBlurPlaceholder = (
  ctx: CanvasRenderingContext2D,
  overlay: BlurOverlay,
  scale: number
) => {
  const { bounds, blur } = overlay;
  const { x, y, width, height } = bounds;
  
  const patternSize = blur.blurType === 'pixelate' ? 8 : 4;
  
  ctx.fillStyle = rgbaToString({ r: 128, g: 128, b: 128, a: 0.3 });
  ctx.fillRect(x * scale, y * scale, width * scale, height * scale);
  
  // Draw grid pattern to represent blur/pixelate
  ctx.strokeStyle = rgbaToString({ r: 99, g: 102, b: 241, a: 0.3 });
  ctx.lineWidth = 0.5;
  
  for (let px = 0; px < width; px += patternSize) {
    ctx.beginPath();
    ctx.moveTo((x + px) * scale, y * scale);
    ctx.lineTo((x + px) * scale, (y + height) * scale);
    ctx.stroke();
  }
  for (let py = 0; py < height; py += patternSize) {
    ctx.beginPath();
    ctx.moveTo(x * scale, (y + py) * scale);
    ctx.lineTo((x + width) * scale, (y + py) * scale);
    ctx.stroke();
  }
  
  // Label
  ctx.fillStyle = rgbaToString({ r: 99, g: 102, b: 241, a: 1 });
  ctx.font = `${10 * scale}px JetBrains Mono`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const label = blur.blurType === 'blur' ? '◉ BLUR' : '▦ PIXELATE';
  ctx.fillText(label, (x + width / 2) * scale, (y + height / 2) * scale);
};

// Draw selection handles
export const drawSelectionHandles = (
  ctx: CanvasRenderingContext2D,
  bounds: Bounds,
  rotation: number,
  scale: number,
  _isHovered: boolean = false
) => {
  const { x, y, width, height } = bounds;
  const handleSize = 8;
  const handleOffset = handleSize / 2;
  
  ctx.save();
  
  // Apply rotation
  if (rotation !== 0) {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    ctx.translate(centerX * scale, centerY * scale);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-centerX * scale, -centerY * scale);
  }
  
  // Draw bounding box
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = 2;
  ctx.setLineDash([]);
  ctx.strokeRect(x * scale, y * scale, width * scale, height * scale);
  
  // Draw handles
  const handles = [
    { pos: 'nw', hx: x, hy: y },
    { pos: 'n', hx: x + width / 2, hy: y },
    { pos: 'ne', hx: x + width, hy: y },
    { pos: 'w', hx: x, hy: y + height / 2 },
    { pos: 'e', hx: x + width, hy: y + height / 2 },
    { pos: 'sw', hx: x, hy: y + height },
    { pos: 's', hx: x + width / 2, hy: y + height },
    { pos: 'se', hx: x + width, hy: y + height },
  ];
  
  handles.forEach(({ hx, hy }) => {
    ctx.fillStyle = 'white';
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.fillRect(
      hx * scale - handleOffset,
      hy * scale - handleOffset,
      handleSize,
      handleSize
    );
    ctx.strokeRect(
      hx * scale - handleOffset,
      hy * scale - handleOffset,
      handleSize,
      handleSize
    );
  });
  
  // Draw rotation handle
  ctx.beginPath();
  ctx.moveTo((x + width / 2) * scale, y * scale);
  ctx.lineTo((x + width / 2) * scale, (y - 25) * scale / scale);
  ctx.strokeStyle = '#6366f1';
  ctx.lineWidth = 1;
  ctx.stroke();
  
  ctx.beginPath();
  ctx.arc((x + width / 2) * scale, (y * scale) - 25, 5, 0, Math.PI * 2);
  ctx.fillStyle = '#6366f1';
  ctx.fill();
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 2;
  ctx.stroke();
  
  ctx.restore();
};

// Main render function
export const renderOverlays = (
  ctx: CanvasRenderingContext2D,
  overlays: OverlayElement[],
  currentTime: number,
  scale: number,
  selectedIds: string[],
  hoveredId: string | null,
  videoElement?: HTMLVideoElement | null,
  /** Called when an asset finished loading and the canvas needs another pass. */
  onNeedsRepaint?: () => void
) => {
  // Clear canvas
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  
  // Sort by zIndex
  const sortedOverlays = [...overlays].sort((a, b) => a.zIndex - b.zIndex);
  
  // Draw each visible overlay at current time
  sortedOverlays.forEach((overlay) => {
    if (!overlay.visible) return;
    if (currentTime < overlay.startTime || currentTime > overlay.endTime) return;
    
    // Calculate animation transform
    const animTransform = calculateAnimationTransform(
      overlay.animation,
      currentTime,
      overlay.startTime,
      overlay.endTime,
      overlay.bounds
    );
    
    // Skip if animation makes overlay invisible
    if (animTransform.opacity <= 0) return;
    
    // Apply animation transform
    ctx.save();
    
    // Only the animation's opacity here — each draw function multiplies in the
    // overlay's own style.opacity, so applying it twice would square it.
    ctx.globalAlpha = animTransform.opacity;
    
    // Apply translation and scale from animation
    if (animTransform.translateX !== 0 || animTransform.translateY !== 0 || animTransform.scale !== 1) {
      const centerX = (overlay.bounds.x + overlay.bounds.width / 2) * scale;
      const centerY = (overlay.bounds.y + overlay.bounds.height / 2) * scale;
      
      ctx.translate(centerX, centerY);
      ctx.translate(animTransform.translateX * scale, animTransform.translateY * scale);
      ctx.scale(animTransform.scale, animTransform.scale);
      ctx.translate(-centerX, -centerY);
    }
    
    switch (overlay.type) {
      case 'rectangle':
      case 'rounded-rectangle':
      case 'circle':
      case 'ellipse':
        drawShape(ctx, overlay as ShapeOverlay, scale);
        break;
      case 'polygon':
        drawPolygon(ctx, overlay as PolygonOverlay, scale);
        break;
      case 'text':
        drawText(ctx, overlay as TextOverlay, scale);
        break;
      case 'image':
        drawImage(ctx, overlay as ImageOverlay, scale, onNeedsRepaint);
        break;
      case 'blur':
        drawBlur(ctx, overlay as BlurOverlay, scale, videoElement ?? null);
        break;
    }
    
    ctx.restore();
    
    // Draw hover highlight
    if (hoveredId === overlay.id && !selectedIds.includes(overlay.id)) {
      ctx.save();
      ctx.strokeStyle = 'rgba(99, 102, 241, 0.5)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        overlay.bounds.x * scale,
        overlay.bounds.y * scale,
        overlay.bounds.width * scale,
        overlay.bounds.height * scale
      );
      ctx.restore();
    }
  });
  
  // Draw selection handles for selected overlays
  sortedOverlays.forEach((overlay) => {
    if (selectedIds.includes(overlay.id)) {
      drawSelectionHandles(ctx, overlay.bounds, overlay.rotation, scale, hoveredId === overlay.id);
    }
  });
};

/**
 * Map a point into an overlay's own un-rotated frame.
 *
 * Hit testing used to compare against the axis-aligned bounds directly, so as
 * soon as an overlay was rotated its clickable area stayed where the overlay
 * used to be — you could see the shape but not grab it.
 */
export const toLocalPoint = (
  overlay: Pick<OverlayElement, 'bounds' | 'rotation'>,
  x: number,
  y: number
): { x: number; y: number } => {
  const { bounds, rotation } = overlay;
  if (!rotation) return { x, y };

  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const radians = (-rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = x - cx;
  const dy = y - cy;

  return {
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  };
};

// Hit testing
export const getOverlayAtPoint = (
  overlays: OverlayElement[],
  x: number,
  y: number,
  currentTime: number
): OverlayElement | null => {
  // Check in reverse order (topmost first)
  const sortedOverlays = [...overlays].sort((a, b) => b.zIndex - a.zIndex);
  
  for (const overlay of sortedOverlays) {
    if (!overlay.visible) continue;
    if (currentTime < overlay.startTime || currentTime > overlay.endTime) continue;
    
    const { bounds } = overlay;
    const local = toLocalPoint(overlay, x, y);
    if (
      local.x >= bounds.x &&
      local.x <= bounds.x + bounds.width &&
      local.y >= bounds.y &&
      local.y <= bounds.y + bounds.height
    ) {
      return overlay;
    }
  }
  
  return null;
};

// Get resize handle at point
export const getResizeHandleAtPoint = (
  overlay: OverlayElement,
  x: number,
  y: number,
  scale: number
): ResizeHandle | null => {
  const { bounds } = overlay;
  const handleSize = 12 / scale; // Handle size in video coordinates

  // Handles are drawn in the overlay's rotated frame, so test the pointer in
  // that same frame instead of against the un-rotated bounds.
  const local = toLocalPoint(overlay, x, y);
  x = local.x;
  y = local.y;
  
  const handles: { handle: ResizeHandle; hx: number; hy: number }[] = [
    { handle: 'nw', hx: bounds.x, hy: bounds.y },
    { handle: 'n', hx: bounds.x + bounds.width / 2, hy: bounds.y },
    { handle: 'ne', hx: bounds.x + bounds.width, hy: bounds.y },
    { handle: 'w', hx: bounds.x, hy: bounds.y + bounds.height / 2 },
    { handle: 'e', hx: bounds.x + bounds.width, hy: bounds.y + bounds.height / 2 },
    { handle: 'sw', hx: bounds.x, hy: bounds.y + bounds.height },
    { handle: 's', hx: bounds.x + bounds.width / 2, hy: bounds.y + bounds.height },
    { handle: 'se', hx: bounds.x + bounds.width, hy: bounds.y + bounds.height },
  ];
  
  // Check rotation handle
  const rotateX = bounds.x + bounds.width / 2;
  const rotateY = bounds.y - 25 / scale;
  if (
    Math.abs(x - rotateX) < handleSize &&
    Math.abs(y - rotateY) < handleSize
  ) {
    return 'rotate';
  }
  
  // Check resize handles
  for (const { handle, hx, hy } of handles) {
    if (Math.abs(x - hx) < handleSize && Math.abs(y - hy) < handleSize) {
      return handle;
    }
  }
  
  return null;
};

